/**
 * Maps the `/performance-trend` response — per-arm cumulative lift % already
 * computed server-side — onto the trend chart's `{ day, testA, testB, control }`
 * point shape. CONTROL is never returned by the endpoint: it's always the
 * implicit 0% baseline the other arms are expressed against.
 */

import type { AnalyticsPerformanceTrendResponse } from '../types/analytics.types';
import type { ArmSlot, PerformanceResponse } from '../types/experiment.types';

import { armSlotForLetter } from './arm-labels';

export function mapPerformanceTrendToChartData(
  response: AnalyticsPerformanceTrendResponse,
): PerformanceResponse {
  const pointsBySlot = new Map<'testA' | 'testB', Map<string, number | null>>();
  const armsPresent: ArmSlot[] = ['control'];

  for (const arm of response.arms) {
    // This endpoint identifies arms by domain letter (`B` / `C`), so `B` is the
    // *first* variant slot. Control (`A`) is never returned — it's the implicit
    // 0% baseline — and is skipped rather than plotted as a variant.
    const slot = armSlotForLetter(arm.arm);
    if (slot == null || slot === 'control') continue;
    armsPresent.push(slot);
    pointsBySlot.set(slot, new Map(arm.series.map((p) => [p.event_date, p.cum_lift_pct])));
  }

  // Union + sort every date across arms so a gap in one arm's series can't
  // shift the other arm's points onto the wrong day index.
  const allDates = new Set<string>();
  for (const dateMap of pointsBySlot.values()) {
    for (const date of dateMap.keys()) allDates.add(date);
  }
  const sortedDates = [...allDates].sort();

  const series = sortedDates.map((date, idx) => ({
    day: idx + 1,
    control: 0, // CONTROL is the baseline; lift relative to itself is 0.
    testA: pointsBySlot.get('testA')?.get(date) ?? 0,
    testB: pointsBySlot.get('testB')?.get(date) ?? 0,
  }));

  const firstDay = series[0]?.day ?? 1;
  const lastDay = series[series.length - 1]?.day ?? 1;

  return {
    metric: response.metric,
    yAxisLabel: '', // resolved upstream by the panel via i18n key.
    series,
    // Annotations come from workflow-service checkpoints, not analytics —
    // leave empty here. The trend chart already handles a blank list.
    annotations: [],
    startDay: firstDay,
    endDay: lastDay,
    armsPresent,
  };
}
